import dayjs from 'dayjs';
import { CALENDAR_CONSTANTS } from './constants';

export const generateTimeSlots = (startHour, endHour, intervalMinutes = 60) => {
  const slots = [];
  let currentMinutes = startHour * 60;
  const endMinutes = endHour * 60;

  while (currentMinutes < endMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const minutes = currentMinutes % 60;
    slots.push({
      time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
      hours,
      minutes,
      totalMinutes: currentMinutes,
    });
    currentMinutes += intervalMinutes;
  }

  return slots;
};

export const getTimeRange = (workingHours = []) => {
  if (!workingHours || workingHours.length === 0) {
    return {
      startHour: CALENDAR_CONSTANTS.DEFAULT_START_HOUR,
      endHour: CALENDAR_CONSTANTS.DEFAULT_END_HOUR,
    };
  }

  let earliestStart = 24;
  let latestEnd = 0;

  workingHours.forEach((wh) => {
    if (wh.startTime) {
      const startHour = parseInt(wh.startTime.split(':')[0], 10);
      earliestStart = Math.min(earliestStart, startHour);
    }
    if (wh.endTime) {
      const endHour = parseInt(wh.endTime.split(':')[0], 10);
      latestEnd = Math.max(latestEnd, endHour + 1);
    }
  });

  return {
    startHour: earliestStart === 24 ? CALENDAR_CONSTANTS.DEFAULT_START_HOUR : earliestStart,
    endHour: latestEnd === 0 ? CALENDAR_CONSTANTS.DEFAULT_END_HOUR : Math.min(latestEnd, 24),
  };
};

export const timeToPixels = (time, startHour, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
  const [hours, minutes] = time.split(':').map(Number);
  const totalMinutes = (hours - startHour) * 60 + minutes;
  return (totalMinutes / 60) * hourHeight;
};

export const pixelsToTime = (pixels, startHour, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
  const totalMinutes = (pixels / hourHeight) * 60;
  const hours = Math.floor(totalMinutes / 60) + startHour;
  const minutes = Math.round(totalMinutes % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const snapToGrid = (minutes, snapMinutes = CALENDAR_CONSTANTS.GRID_SNAP_MINUTES) => {
  return Math.round(minutes / snapMinutes) * snapMinutes;
};

export const formatTime = (dateTime) => {
  return dayjs(dateTime).format('HH:mm');
};

export const formatTimeRange = (startTime, endTime) => {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

export const getEventDuration = (startTime, endTime) => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  const diffMinutes = end.diff(start, 'minute');
  
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }
  if (minutes === 0) {
    return `${hours} godz.`;
  }
  return `${hours} godz. ${minutes} min`;
};

export const isTimeInRange = (time, startTime, endTime) => {
  const [checkH, checkM] = time.split(':').map(Number);
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);

  const checkMinutes = checkH * 60 + checkM;
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  return checkMinutes >= startMinutes && checkMinutes < endMinutes;
};

export const getCurrentTimePosition = (startHour, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
  const now = dayjs();
  const hours = now.hour();
  const minutes = now.minute();

  if (hours < startHour) return null;

  return timeToPixels(`${hours}:${minutes}`, startHour, hourHeight);
};
